import { Column } from '../models/column';

/**
 * Type to define "contextualizef" fn which receives a column and applies
 * a transformation to the field path that will be required for deep levels
 * (like in tables we need to trim the path of a section)
 */
export type ColumnContextualizer = <T extends Column = Column>(x?: T) => string;

/**
 * Removes first context sub path. It will return the original field if it
 * doesn't contain at least one slash
 * @example
 * output from: 'a/b/c' => 'b/c'
 * output from: 'a' => 'a'
 */
const defaultContexualizer: ColumnContextualizer = <T extends Column = Column>(column: T) => {
  if (!column.field.includes('/')) {
    return column.field;
  }

  // Ignore the first context
  const [, ...rest] = column.field.split('/');
  return rest.join('/');
};

/**
 * Checks for column.path. If the path contains expression then keep it and prepend it with \'**\'
 * for JSONata to check expression from root level.
 * @example
 * output from: a.b[exp_1].c => b[exp_1].c
 * output from: a[exp_1].b => **[exp_1].b
 */

const pathContextualizer: ColumnContextualizer = <T extends Column = Column>(column: T) => {
  const [firstPath, ...rest] = column.path.split('.');

  if (firstPath.includes('[') && firstPath.includes(']') && !firstPath.startsWith('**')) {
    const firstValue = firstPath.substring(firstPath.indexOf('['));
    const newfirstPath = `**${firstValue}`;

    return `${newfirstPath}.${rest.join('.')}`;
  }
  return rest.join('.');
};

/**
 * When contextualizer is a string we build a function that replace the first found token
 * in "field" with empty string
 * @example
 * const tokenContextualizer = strContextualizerFactory('a')
 * tokenContextualizer('a/b.c') // => 'b.c'
 *
 * const anotherTokenContextualizer = strContextualizerFactory('a')
 * tokenContextualizer('a/b/c') // => 'b/c'
 */
const strContextualizerFactory = (token: string): ColumnContextualizer => {
  return (column: Column): string => {
    return column.field.replace(`${token}/`, '');
  };
};

/**
 * Column contextualizer mapper
 * @param contextualizerFn
 * @returns
 */
function makeColumnContextualizerCallback(contextualizerFn: ColumnContextualizer): <T extends Column>(a: T) => T {
  function contextualizeColumn<T extends Column>(column: T): T {
    return {
      ...column,
      field: contextualizerFn(column),
      path: column.path ? pathContextualizer(column) : column.path,
      unit: column.unit ? contextualizeColumn(column.unit) : undefined,
    };
  }
  return contextualizeColumn;
}

/**
 * transforms the `field` value by trimming the value of field by replacing the
 * contextualizer "token" or applying the given ColumnContextualizer function
 */
export function contextualizeColumnsField<T extends Column = Column>(
  columns: T[],
  contextualizerParam: string | ColumnContextualizer = defaultContexualizer
): T[] {
  return columns.map(col => contextualizeSingleColumnField(col, contextualizerParam));
}

/**
 * transforms the `field` value by trimming the value of field by replacing the
 * contextualizer "token" or applying the given ColumnContextualizer function
 */
export function contextualizeSingleColumnField<T extends Column = Column>(
  columns: T,
  contextualizerParam: string | ColumnContextualizer = defaultContexualizer
): T {
  let contextualizerFn: ColumnContextualizer;
  if (contextualizerParam) {
    contextualizerFn =
      typeof contextualizerParam === 'string' ? strContextualizerFactory(contextualizerParam) : contextualizerParam;
  }
  const toContextualizedColumnFn = makeColumnContextualizerCallback(contextualizerFn);
  return toContextualizedColumnFn(columns);
}
